import React, { useMemo, useState } from 'react';
import { Network, Plus, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { jsonApi } from '@/lib/jsonApi';
import { Panel } from '@/components/responseDetail/Panel';
import FactorTagRow from '@/components/responseDetail/FactorTagRow';
import TagHistory from '@/components/responseDetail/TagHistory';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const GROUPS = [
  { key: 'positive_driver', title: 'Positive Drivers', dot: 'bg-[#2E9A6B]' },
  { key: 'gap', title: 'Gaps', dot: 'bg-[#C4353D]' },
];

function AddFactor({ available, tagged, onAdd }) {
  const [open, setOpen] = useState(false);
  const options = available.filter((f) => !tagged.has(f.factor_id));
  const byAttr = options.reduce((m, f) => { (m[f.attribute_name] = m[f.attribute_name] || []).push(f); return m; }, {});
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 text-xs font-medium border border-[#CBD5E1] text-[#475569] hover:border-[#1A3E6F] hover:text-[#1A3E6F] rounded-lg px-2.5 py-1.5 transition-colors" data-testid="factor-tag-add-btn">
          <Plus className="w-3.5 h-3.5" /> Add tag
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0 max-h-80 overflow-y-auto" data-testid="factor-tag-picker">
        {options.length === 0 && <p className="text-xs text-[#94A3B8] p-3">No more factors to add.</p>}
        {Object.entries(byAttr).map(([attr, list]) => (
          <div key={attr} className="py-1 border-b border-[#F1F5F9] last:border-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] px-3 py-1">{attr}</p>
            {list.map((f) => (
              <button key={f.factor_id} type="button" onClick={() => { setOpen(false); onAdd(f.factor_id); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8FAFC] flex items-center justify-between gap-2" data-testid={`factor-tag-option-${f.factor_id}`}>
                <span>{f.factor_name}</span>
                <span className={`text-[10px] font-semibold ${f.classification === 'gap' ? 'text-[#A1272E]' : 'text-[#1F684A]'}`}>{f.classification === 'gap' ? 'Gap' : 'Positive Driver'}</span>
              </button>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const emptyText = (available, run) => {
  if (available.length === 0) return 'No active Attributes & Factors apply to this survey yet.';
  if (run?.status === 'no_feedback') return 'No written or transcribed feedback to tag.';
  if (run?.status === 'done') return 'AI found no matching factors in the feedback.';
  return 'No factors tagged yet.';
};

export default function FactorTagsPanel({ responseRef, initial, available, run, canEdit }) {
  const [tags, setTags] = useState(initial.factor_tags || []);
  const [removed, setRemoved] = useState(initial.removed_factor_tags || []);
  const [lastRun, setLastRun] = useState(run);
  const [busy, setBusy] = useState(false);
  const tagged = useMemo(() => new Set(tags.map((t) => t.factor_id)), [tags]);
  const base = `/responses/${encodeURIComponent(responseRef)}/factor-tags`;
  const act = async (path, method, body, okMsg) => {
    setBusy(true);
    try {
      const d = await jsonApi(path, method, body);
      setTags(d.factor_tags || []);
      setRemoved(d.removed_factor_tags || []);
      if (d.factor_tagging_run) setLastRun(d.factor_tagging_run);
      if (okMsg) toast.success(typeof okMsg === 'function' ? okMsg(d) : okMsg);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!tags.length && !removed.length && !canEdit) return null;
  return (
    <Panel title="Attributes & Factors" icon={Network} testId="factor-tags-panel"
      action={canEdit && (
        <div className="flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />}
          {available.length > 0 && (
            <button type="button" disabled={busy} onClick={() => act(`${base}/auto`, 'POST', null, (d) => `AI review done · ${d.factor_tagging_run?.tagged || 0} new tag(s)`)}
              className="inline-flex items-center gap-1 text-xs text-[#1A3E6F] hover:underline disabled:opacity-50" data-testid="factor-tag-rerun-btn">
              <Sparkles className="w-3.5 h-3.5" /> {lastRun ? 'Re-run AI' : 'Run AI'}
            </button>
          )}
          {available.length > 0 && <AddFactor available={available} tagged={tagged} onAdd={(fid) => act(base, 'POST', { factor_id: fid }, 'Tag added')} />}
        </div>
      )}>
      {GROUPS.map((g) => {
        const list = tags.filter((t) => t.classification === g.key);
        if (!list.length) return null;
        return (
          <div key={g.key} className="mb-5 last:mb-0" data-testid={`factor-group-${g.key}`}>
            <p className="text-xs font-bold text-[#334155] mb-2 flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${g.dot}`} />{g.title} <span className="text-[#94A3B8] font-semibold">{list.length}</span></p>
            {list.map((t) => (
              <FactorTagRow key={t.factor_id} tag={t} canEdit={canEdit} busy={busy}
                onConfirm={(fid) => act(`${base}/${fid}/confirm`, 'POST', null, 'Tag confirmed')}
                onRemove={(fid) => act(`${base}/${fid}`, 'DELETE', null, 'Tag removed · kept in tag history')} />
            ))}
          </div>
        );
      })}
      {!tags.length && <p className="text-sm text-[#94A3B8]" data-testid="factor-tags-empty">{emptyText(available, lastRun)}</p>}
      <TagHistory removed={removed} />
    </Panel>
  );
}
